/* eslint-disable prettier/prettier */
import { format } from 'date-fns';
import React, { useCallback, useEffect, useState } from 'react';
import { Button, NavDropdown } from 'react-bootstrap';
import {
  FaBarcode,
  FaCheckCircle,
  FaCircle,
  FaSpinner,
  FaWhatsapp,
} from 'react-icons/fa';
import { IContaPR } from '..';
import { useToast } from '../../../hooks/toast';
import api from '../../../services/api';
import { Colors } from '../../../styles/constants';
import { htmlDecode, updateTimeToNow } from '../../../utils/funcoes';
import getResponseErrors from '../../../utils/getResponseErrors';
import { Spinner } from '../../Agendamento/styles';
import { useClinica } from '../../../hooks/clinica';

// import { Container } from './styles';

export interface IRetornoBoletoPaciente {
  nome: string;
  email: string;
  cpf: string;
  link: string;
  celular: string;
  dataEnvio?: Date;
  dataEnvioFormatado?: string;
  textoMensagem: string;
  historico: string;
  valor: number;
  dataVenc: Date;
  CprCodigo: number;
  CprChave: string;
}

type Props = {
  cprCodigo: number;
  contaPR: IContaPR;
  atualizado?: boolean;
  dadosBoleto?: IRetornoBoletoPaciente;
  inLine?: boolean;
  // handleFechar(): void;
};

const Boleto: React.FC<Props> = ({
  cprCodigo,
  contaPR,
  atualizado = false,
  dadosBoleto,
  inLine,
}) => {
  const { addToast } = useToast();

  const { dataHoraServidor } = useClinica();

  const [boletoPaciente, setBoletoPaciente] = useState<
    IRetornoBoletoPaciente | undefined
  >(dadosBoleto);
  const [loading, setLoading] = useState(false);
  const [dataUltimoEnvioFormatado, setDataUltimoEnvioFormatado] = useState('');

  useEffect(() => {
    async function loadBoleto(): Promise<void> {
      setLoading(true);
      try {
        const response = await api.get<IRetornoBoletoPaciente>(
          `/pagamento/buscaBoleto/${cprCodigo}`,
        );

        setBoletoPaciente(response.data);
        setDataUltimoEnvioFormatado(response.data.dataEnvioFormatado || '');
      } catch (error) {
        const mensagemErro = getResponseErrors(error);
        console.log(mensagemErro);
      }
      setLoading(false);
    }
    if (cprCodigo && !dadosBoleto) loadBoleto();
  }, [addToast, cprCodigo, atualizado, dadosBoleto]);

  const handleGerarBoleto = useCallback(async () => {
    if (!boletoPaciente || boletoPaciente.cpf === '') {
      addToast({
        title: 'Não foi possível gerar boleto',
        type: 'error',
        description: 'Sem cpf cadastrado.',
      });
      return;
    }
    try {
      setLoading(true);
      const response = await api.put(
        `/caixa/pagamentoonline/${contaPR.CprChave}`,
        {
          //   issuer_id,
          payment_method_id: 'boleto',
          transaction_amount: (
            contaPR.CprValor +
            contaPR.CprMulta +
            contaPR.CprJuros
          ).toFixed(2),
          installments: 1,
          external_reference: contaPR.CprChave,
          description: `${contaPR.CprCodOrigem}-${contaPR.CprHistorico}`,
          payer: {
            name: boletoPaciente?.nome || '',
            email: boletoPaciente?.email || '',
            identification: {
              type: 'cpf',
              number: boletoPaciente?.cpf || '',
            },
          },
        },
      );

      if (response.data.urlBoleto && response.data.urlBoleto !== '') {
        setBoletoPaciente(state => {
          return {
            ...state,
            nome: state?.nome || '',
            email: state?.email || '',
            cpf: state?.cpf || '',
            celular: state?.celular || '',
            link: response.data.urlBoleto,
            textoMensagem: state?.textoMensagem || '',
            historico: state?.historico || '',
            valor: state?.valor || 0,
            dataVenc:
              state?.dataVenc ||
              updateTimeToNow(new Date(dataHoraServidor.toString())),
            dataEnvioFormatada: dataUltimoEnvioFormatado,
            CprChave: state?.CprChave || '',
            CprCodigo: state?.CprCodigo || 0,
          };
        });
      }
    } catch (error) {
      const mensagemErro = getResponseErrors(error);
      addToast({
        title: 'Não foi gerar boleto',
        type: 'error',
        description: mensagemErro,
      });
    }
    setLoading(false);
  }, [
    boletoPaciente,
    contaPR,
    addToast,
    dataUltimoEnvioFormatado,
    dataHoraServidor,
  ]);

  const handleEnviaBoleto = useCallback(() => {
    if (boletoPaciente?.celular && boletoPaciente?.celular !== '') {
      const textoDecode = htmlDecode(boletoPaciente?.textoMensagem || '');
      const textoMensagem = encodeURIComponent(textoDecode);

      const linkWhatsapp = `https://api.whatsapp.com/send?phone=55${boletoPaciente?.celular}&text=${textoMensagem}`;
      window.open(linkWhatsapp, '_blank');
    }
  }, [boletoPaciente]);

  const handleSalvaEnvio = useCallback(async () => {
    try {
      setLoading(true);
      const dados = {
        EnvCprCodigo: cprCodigo,
        EnvMetodo: 'Whatsapp',
      };
      await api.post(`/pagamento/envioboleto`, dados);
      setDataUltimoEnvioFormatado(
        format(
          updateTimeToNow(new Date(dataHoraServidor.toString())),
          'dd/MM/yyyy HH:mm',
        ),
      );
    } catch (error) {
      const mensagemErro = getResponseErrors(error);
      addToast({
        title: 'Não foi possível salvar o envio',
        type: 'error',
        description: mensagemErro,
      });
    } finally {
      setLoading(false);
    }
  }, [addToast, cprCodigo, dataHoraServidor]);

  return (
    <>
      <NavDropdown
        // eslint-disable-next-line prettier/prettier
        title={
          // eslint-disable-next-line react/jsx-wrap-multilines
          <>
            <FaBarcode
              color={`${boletoPaciente !== undefined && boletoPaciente.link !== ''
                ? Colors.primary
                : Colors.danger
                }`}
            />
          </>
        }
        id="dropdown-Func"
        alignRight
      >
        <div className={`${inLine ? 'd-inline-flex' : ''}`}>
          {boletoPaciente !== undefined && boletoPaciente.link !== '' ? (
            <>
              <NavDropdown.ItemText className="p-1">
                <div className="dropdown-item text-dark">
                  <a
                    href={boletoPaciente.link}
                    title="Ver Boleto"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-sm btn-outline-primary w-100"
                  >
                    <FaBarcode /> Ver Boleto
                  </a>
                </div>
              </NavDropdown.ItemText>
            </>
          ) : (
            ''
          )}
          {boletoPaciente !== undefined && boletoPaciente.link === '' ? (
            <>
              <NavDropdown.ItemText className="p-1">
                <div className="dropdown-item text-dark">
                  <Button
                    className=" w-100"
                    type="button"
                    data-id={`boleto-${cprCodigo}`}
                    size="sm"
                    variant="outline-danger"
                    onClick={() => handleGerarBoleto()}
                    title="gerar Boleto"
                  >
                    {loading ? (
                      <Spinner>
                        <FaSpinner />
                      </Spinner>
                    ) : (
                      <FaBarcode />
                    )}
                    <span> Gerar Boleto</span>
                  </Button>
                </div>
              </NavDropdown.ItemText>
            </>
          ) : (
            ''
          )}

          <NavDropdown.Divider className="m-0" />

          {boletoPaciente !== undefined && boletoPaciente.link !== '' ? (
            <>
              <NavDropdown.ItemText className="p-1">
                <div className="dropdown-item text-dark">
                  <Button
                    type="button"
                    variant="success"
                    size="sm"
                    className="w-100"
                    data-id={contaPR.CprCodigo}
                    onClick={() => handleEnviaBoleto()}
                  >
                    <FaWhatsapp size={16} /> Enviar
                  </Button>
                </div>
              </NavDropdown.ItemText>
            </>
          ) : (
            ''
          )}

          {boletoPaciente !== undefined && boletoPaciente.link !== '' ? (
            <>
              <NavDropdown.ItemText className="p-1">
                <div className="dropdown-item text-dark">
                  {loading ? (
                    <Spinner className="px-2 py-1 border rounded ml-1">
                      <FaSpinner size={17} />
                    </Spinner>
                  ) : (
                    <Button
                      type="button"
                      variant={
                        dataUltimoEnvioFormatado !== ''
                          ? 'success'
                          : 'outline-secondary'
                      }
                      size="sm"
                      className="w-100"
                      data-id={contaPR.CprCodigo}
                      onClick={() => handleSalvaEnvio()}
                      title={dataUltimoEnvioFormatado}
                    >
                      {dataUltimoEnvioFormatado !== '' ? (
                        <FaCheckCircle size={16} />
                      ) : (
                        <FaCircle size={16} />
                      )}{' '}
                      Enviado
                    </Button>
                  )}
                </div>
              </NavDropdown.ItemText>
            </>
          ) : (
            ''
          )}
        </div>
      </NavDropdown>
    </>
  );
};

export default Boleto;
